import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { t } from 'i18next';

import { ArrowLeft, ArrowRightSmall, Xmark } from '@peacecover/icon';
import { SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { QUERY_KEYS } from '../image-search/constants/queryKeys';
import type { ImageSearchResponseType } from '../image-search/types/type';
import PhotoCardPost from '../components/card/PhotoCardPost';
import { getPhotoCardBadges } from './utils/getPhotoCardBadges';
import useUserQuery from '../hooks/queries/useUserQuery';
import BottomCTA from './components/BottomCTA';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { queryGet } from '@pocapan/shared/utils/queryClient';
import addTradingPhotoCards from '../../components/api/mutations/addTradingPhotoCards';

const SIMILARITY_THRESHOLD = 0.8;

const SetExchangeCardPage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const showSuccessToast = useSuccessPhotoCardToast();
  const queryClient = useQueryClient();
  const { data: user } = useUserQuery();

  const searchResult = queryClient.getQueryData<ImageSearchResponseType>(
    QUERY_KEYS.IMAGE_SEARCH_RESULT
  );

  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [reportingIndex, setReportingIndex] = useState<number | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  const selectedMember = JSON.parse(
    sessionStorage.getItem('exchange-mode-selected-member') ?? 'null'
  );

  useEffect(() => {
    if (!searchResult) {
      navigate(-1);
    }
  }, [searchResult, navigate]);

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'reported-new-photo-cards') return;

      const { photoCardId } = event.data.params;
      const res = await queryGet(`/api/v1/photo_cards/${photoCardId}`);
      const newPhotoCard = res?.photoCard;

      if (reportingIndex === null || !newPhotoCard) return;

      queryClient.setQueryData<ImageSearchResponseType>(QUERY_KEYS.IMAGE_SEARCH_RESULT, (old) => {
        if (!old) return old as unknown as ImageSearchResponseType;
        const updatedResults = old.results.map((result, i) =>
          i === reportingIndex
            ? {
                ...result,
                candidates: [
                  { ...result.candidates[0], photoCard: newPhotoCard, similarity: 1 }
                ] as typeof result.candidates
              }
            : result
        );
        return { ...old, results: updatedResults };
      });
      setReportingIndex(null);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [reportingIndex, queryClient]);

  if (!searchResult || !user) return null;

  const handleRegister = async () => {
    const tradingCards = searchResult.results
      .filter((_, i) => !excludedIndices.has(i))
      .flatMap(({ candidates }) => {
        const candidate = candidates[0];
        const photoCard = candidate?.photoCard;
        if (!photoCard || (candidate.similarity ?? 0) < SIMILARITY_THRESHOLD) return [];
        return [{ artistId: photoCard.artistId, photoCard, category: 'exchange', price: null }];
      });

    if (!tradingCards.length) {
      showSuccessToast('bottom', t('SetExchangeCardPage.emptyToast'), true);
      return;
    }

    setIsRegistering(true);
    try {
      await addTradingPhotoCards(user.tradingUserProfile.userProfileId, tradingCards);
      navigate('/tradings/completed_add?isExchange=true');
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <main className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))]">
      <header className="fixed top-0 left-0 right-0 flex items-center px-[16px] pb-[15px] bg-pure-white dark:bg-background-dark z-10 pt-[calc(env(safe-area-inset-top)+8px)]">
        <ArrowLeft className="text-text-main-lv1" onClick={handleHistoryBack} />
      </header>
      <div className="mt-[69px] px-[24px] pb-[120px] flex flex-col">
        <div className="flex flex-col items-start gap-[14px] py-[10px]">
          <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1">
            {t('SetExchangeCardPage.title')}
          </h1>
        </div>
        <div className="flex flex-col gap-[16px] mt-[38px]">
          {searchResult.results.map((result, index) => {
            if (excludedIndices.has(index)) return null;

            const candidate = result.candidates[0];
            const photoCard =
              (candidate?.similarity ?? 0) >= SIMILARITY_THRESHOLD
                ? candidate?.photoCard
                : undefined;

            if (photoCard) {
              const title = t('NewTradingPhotoCards.photoCardTitle', {
                artist: photoCard.memberName,
                category: photoCard.categoryName,
                categoryEmoji: photoCard.categoryEmoji
              });
              return (
                <PhotoCardPost
                  key={index}
                  title={title}
                  subtitle={photoCard.eventName}
                  imageUrl={photoCard.pictureUrl}
                  showInput={false}
                  showClose={true}
                  badges={getPhotoCardBadges(photoCard)}
                  onClose={() => setExcludedIndices((prev) => new Set([...prev, index]))}
                />
              );
            }

            return (
              <UnregisteredPhotoCard
                key={candidate?.photoCardId ?? `unregistered-${index}`}
                imageUrl={result.originalUrl}
                onReport={() => {
                  setReportingIndex(index);
                  sessionStorage.setItem('report-prefilled-image', result.originalUrl ?? '');
                  openNewTab(
                    `/report-photo-card/event-activities?newTabOpen=true&artistId=${selectedMember?.artistId ?? ''}&memberId=${selectedMember?.memberId ?? ''}`
                  );
                }}
                onClose={() => setExcludedIndices((prev) => new Set([...prev, index]))}
              />
            );
          })}
        </div>
      </div>

      <BottomCTA onClick={handleRegister} disabled={isRegistering}>
        {t('SetExchangeCardPage.registerButton')}
      </BottomCTA>
    </main>
  );
};

const UnregisteredPhotoCard = ({
  imageUrl,
  onReport,
  onClose
}: {
  imageUrl?: string;
  onReport: () => void;
  onClose: () => void;
}) => (
  <div className="relative w-full bg-background-light dark:bg-background-elevated rounded-[12px] shadow-[inset_0_0_0_1px_var(--color-separator1)] pl-[16px] pr-[10px] py-[16px] flex flex-col">
    <div className="flex flex-row gap-[14px] items-center">
      <ThumbnailPhotoCard imageUrl={imageUrl ?? ''} className="w-[48px] h-[75px]" />
      <button
        type="button"
        className="absolute top-[10px] right-[10px] p-[6px] flex items-center justify-center bg-secondary-light dark:bg-secondary-dark rounded-full"
        onClick={onClose}
      >
        <Xmark size={16} className="text-on-secondary" />
      </button>
      <div className="flex flex-col gap-[7px] leading-none">
        <p className="text-[15px] font-semibold text-text-main-lv1">{t('SetExchangeCardPage.unregisteredTitle')}</p>
        <p className="text-[13px] font-medium text-text-main-lv3">
          {t('SetExchangeCardPage.unregisteredDescription')}
        </p>
        <SquareBadge
          colorVariant="secondary"
          onClick={onReport}
          className="pl-[8px] pr-[3px] py-[3px]"
        >
          {t('SetExchangeCardPage.unregisteredButton')}
          <ArrowRightSmall size={12} className="ml-[2px]" />
        </SquareBadge>
      </div>
    </div>
  </div>
);

export default SetExchangeCardPage;
